/* eslint-disable jira/filename-case */
import { useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import { expVal } from '@atlassian/jira-feature-experiments';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { FlagConfiguration } from '@atlassian/jira-flags';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fireIssueCreateErrorAnalytics as fireErrorAnalyticsDefault } from '@jira/issue-create__issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import {
	MULTI_INPUT_FIELDS_ERROR_KEYS,
	GLOBAL_EVENT_NAMESPACE,
} from '@jira/issue-create__issue-create-common-types/src/common/constants/index.tsx';
import type {
	FieldTabs,
	FieldConfig,
	FormFieldValues,
} from '@jira/issue-create__issue-create-common-types/src/common/types/index.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@jira/issue-create__issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import type { TriggerPointKeyType } from '@jira/issue-create__use-trigger-issue-create-modal/src/types.tsx';
import { JIRA_SOFTWARE } from '@jira/platform__application-editions/src/application.tsx';
import {
	FREE_EDITION,
	STANDARD_EDITION,
} from '@jira/platform__application-editions/src/edition.tsx';
import { fg } from '@jira/platform__feature-gating';
import getEditions from '@jira/platform__get-tenant-context/src/edition/index.tsx';
import { apiReady as eventsApiReady } from '@jira/platform__legacy-create-issue-legacy/src/apiReady.tsx';
import { defineMessage } from '@jira/platform__react-intl';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { getWindow } from '@jira/ssr__browser-globals/src/getWindow.tsx';
import type { CreatedIssueWithIssueData, CreatedIssues } from '../../common/types/index.tsx';
import type { IssueCreateResponse } from '../../services/create-issue/types.tsx';
import { executeEcosystemAPICallback } from './execute-ecosystem-api-callback/executeEcosystemAPICallback.tsx';
import type { Params, CreatedIssuesDataByKey } from './types.tsx';

const watchersWarningFlagTitle = defineMessage({
	id: 'issue-create.modal.watchers-warning-flag-title',
	defaultMessage: 'Watchers not added to your issue',
	description: 'Title for error flag when issue is created but watchers has warning',
});

const watchersErrorFlagTitle = defineMessage({
	id: 'issue-create.modal.watchers-error-flag-title',
	defaultMessage: 'Something went wrong',
	description: 'Title for error flag when issue is created but watchers has error',
});

const watchersWarningFlagTitleIssueTermRefresh = defineMessage({
	id: 'issue-create.modal.watchers-warning-flag-title-issue-term-refresh',
	defaultMessage: 'Watchers not added to your work item',
	description: 'Title for error flag when issue is created but watchers has warning',
});

const watchersWarningFlagTitleIssueTerminologyRefreshM4 = defineMessage({
	id: 'issue-create.modal.watchers-warning-flag-title-issue-terminology-refresh-m4',
	defaultMessage: 'Watchers not added to the {issueType} {issueKey}',
	description:
		'Title for error flag when issue is created but watchers has warning. Please translate as “work type {issueType}”',
});

export const ISSUE_SEARCH_URL = '/rest/api/3/search/jql';

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
let issueDataTillNow: Record<string, any> = {};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const issueIdsTillNow: Record<string, any> = {};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
let activePromises: Record<string, any> = {};

export const useStorageVariables = (): {
	cloudId: string;
	applicationKey: 'JIRA_SOFTWARE';
	shouldCheckForStorageLimit: boolean;
} => {
	const cloudId = useCloudId();
	const { hasSoftwareAccess } = useApplicationPermissions();

	return useMemo(() => {
		const softwareEdition = getEditions(getMeta).software;

		return {
			cloudId,
			applicationKey: JIRA_SOFTWARE,
			shouldCheckForStorageLimit:
				hasSoftwareAccess &&
				(softwareEdition === FREE_EDITION ||
					// eslint-disable-next-line jira/ff/no-preconditioning -- Both rollout safeguards are intentionally required for Jira Standard.
					(softwareEdition === STANDARD_EDITION &&
						fg('jira-standard-storage-hard-limits-create-form-fg') &&
						fg('jira-standard-storage-hard-limits-issue-query-fg'))),
		};
	}, [cloudId, hasSoftwareAccess]);
};

const postMessageToConsumers = (
	createdIssues: CreatedIssues,
	triggerPointKey?: TriggerPointKeyType,
) => {
	const browserWindow = getWindow();
	if (!browserWindow) return;
	if (triggerPointKey === 'issue-create-embed-trigger') {
		browserWindow.parent?.postMessage(
			{
				event: 'jiraIssueCreate.sessionComplete',
				payload: createdIssues,
				triggerPointKey,
			},
			'*',
		);
	}
	browserWindow.postMessage(
		{
			event: 'jiraIssueCreate.sessionComplete',
			payload: createdIssues,
			triggerPointKey,
		},
		'*',
	);
};

// using this function to find out the first empty required field.
// To do that passing fields as a string[] present in the modal and empty fields, which are marked as required as a string[]
export const getFirstFieldIdWithError = ({
	fields,
	emptyRequiredFields,
}: {
	fields: string[];
	emptyRequiredFields: string[];
}): string | undefined => {
	if (emptyRequiredFields) {
		return fields.find((fieldId: string) => {
			if (emptyRequiredFields.includes(fieldId)) {
				return true;
			}
			// handling multi input field errors separately because it contains different error keys
			// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly worklog: readonly ["worklog_adjustmentAmount", "worklog_adjustEstimate", "worklog_activate", "worklog_timeLogged", "worklog_newEstimate", "worklog_startDate", "worklog_description"]; readonly issuelinks: readonly [...]; }'.
			if (MULTI_INPUT_FIELDS_ERROR_KEYS[fieldId]) {
				return emptyRequiredFields.some((key) =>
					// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly worklog: readonly ["worklog_adjustmentAmount", "worklog_adjustEstimate", "worklog_activate", "worklog_timeLogged", "worklog_newEstimate", "worklog_startDate", "worklog_description"]; readonly issuelinks: readonly [...]; }'.
					MULTI_INPUT_FIELDS_ERROR_KEYS[fieldId].includes(key),
				);
			}
			return false;
		});
	}
	return undefined;
};

const fetchIssueListPromise = (
	jql: string,
	fieldsToAddInPublishedData: string[],
	fetchAllFields: boolean,
	issueIdsToReconcileOn: number[],
) => {
	if (activePromises[jql] === undefined) {
		if (fetchAllFields) {
			activePromises[jql] = performPostRequest(ISSUE_SEARCH_URL, {
				body: JSON.stringify({
					jql,
					fields: [...new Set(['*all'])],
					reconcileIssues: issueIdsToReconcileOn,
				}),
			});
		} else {
			activePromises[jql] = performPostRequest(ISSUE_SEARCH_URL, {
				body: JSON.stringify({
					jql,
					fields: [
						...new Set([
							'summary',
							'status',
							'assignee',
							'duedate',
							'project',
							'issuetype',
							'priority',
							'issuelinks',
							...fieldsToAddInPublishedData,
						]),
					],
					reconcileIssues: issueIdsToReconcileOn,
				}),
			});
		}
	}
	return activePromises[jql];
};
const getIssueList = async (
	createdIssues: CreatedIssues,
	fieldsToAddInPublishedData: string[],
	fetchAllFields: boolean,
) => {
	// Get list of issues that have not been fetched before
	const issuesToFetch = createdIssues.filter(({ issueKey }) => !issueDataTillNow[issueKey]);
	const issueIdsToFetch = createdIssues.filter(({ issueId }) => !issueIdsTillNow[issueId]);
	let newIssueDetails = { issues: [] };
	if (issuesToFetch.length) {
		// API call is made only if there are issues that needs to be fetched
		const jql = issuesToFetch.reduce(
			(jqlQuery, { issueKey }, index) =>
				`${jqlQuery}issuekey=${issueKey}${index !== issuesToFetch.length - 1 ? ' OR ' : ''}`,
			'',
		);
		const issueIdsToReconcileOn = issueIdsToFetch
			.map(({ issueId }) => issueId) // Extract the issueId from each object
			.filter((issueId) => Number.isInteger(issueId)); // Check if the issueId is an integer

		try {
			newIssueDetails = await fetchIssueListPromise(
				jql,
				fieldsToAddInPublishedData,
				fetchAllFields,
				issueIdsToReconcileOn,
			);
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalyticsDefault({
				meta: {
					id: 'getIssueList',
					packageName: 'jiraIssueCreate',
					teamName: 'gryffindor',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});

			activePromises[jql] = undefined;
		}
	}
	const prefetchedIssueDetails = createdIssues
		.filter(({ issueKey }) => issueDataTillNow[issueKey])
		.map(({ issueKey }) => issueDataTillNow[issueKey].createdIssueDetails);

	return { issues: [...prefetchedIssueDetails, ...newIssueDetails.issues] };
};

const merge = (
	obj: CreatedIssuesDataByKey,
	obj2: CreatedIssuesDataByKey,
): CreatedIssuesDataByKey => ({ ...obj, ...obj2 });

export const publishCreatedIssues = async (
	{
		createdIssues,
		globalEventNamespaces = [GLOBAL_EVENT_NAMESPACE],
		fieldsToAddInPublishedData = [],
		alertsToBeLinked = [],
		isSessionComplete = false,
		onIssueCreate,
		siteDomain,
		selectedCloudId,
		onClose,
		triggerPointKey,
		publishGICLifecycle,
		isIssueCreateSuccess,
		callbackPayload,
		jqlSwimlaneId,
		ecosystemCallback,
	}: Params,
	fireErrorAnalytics: typeof fireErrorAnalyticsDefault = fireErrorAnalyticsDefault,
): Promise<void> => {
	let fetchAllFields = false;
	if (ecosystemCallback && ecosystemCallback.callbackFunction) fetchAllFields = true;
	if (createdIssues.length) {
		/**
		 * We need to unregister the SPA global issue create event handlers so that new issue create can use its
		 * default ones which render the success and error flag in a different place
		 */
		let oldFlagUnbindSuccess = false;
		const browserWindow = getWindow();
		try {
			browserWindow?.require('jira/common/header').unbind();
			oldFlagUnbindSuccess = Boolean(browserWindow);
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			oldFlagUnbindSuccess = false;
		}
		try {
			const issueSearchRequest = getIssueList(
				createdIssues,
				fieldsToAddInPublishedData,
				fetchAllFields,
			);
			const [issueList, Events] = await Promise.all([issueSearchRequest, eventsApiReady()]);
			const issueData = keyBy(issueList.issues, 'key');
			const createdIssuesWithIssueData = createdIssues.map<CreatedIssueWithIssueData>(
				(createdIssue) => {
					const createdIssueDetails = issueData[createdIssue.issueKey];
					const summary = createdIssueDetails?.fields?.summary;

					return {
						...createdIssue,
						summary,
						createdIssueDetails,
						selectedCloudId,
						jqlSwimlaneId,
					};
				},
			);

			issueDataTillNow = merge(issueDataTillNow, keyBy(createdIssuesWithIssueData, 'issueKey'));

			if (isIssueCreateSuccess) {
				// ideally createdIssuesWithIssueData will contain a single element
				createdIssuesWithIssueData.forEach((createdIssue) => {
					onIssueCreate?.(
						siteDomain ? { ...createdIssue, siteDomain } : createdIssue,
						alertsToBeLinked,
					);
					publishGICLifecycle?.(GIC_LIFECYCLE_EVENTS.ISSUE_CREATE_SUCCESS, {
						createdIssueData: createdIssue,
						callbackPayload,
					});
				});
			}

			if (isSessionComplete) {
				if (fetchAllFields)
					executeEcosystemAPICallback(ecosystemCallback, createdIssuesWithIssueData);

				postMessageToConsumers(createdIssuesWithIssueData, triggerPointKey);
				if (fg('stop_polli_fails_due_to_load_bridge')) onClose?.(createdIssuesWithIssueData);
				globalEventNamespaces.forEach((namespace) => {
					if (triggerPointKey !== undefined) {
						Events?.trigger?.(`${namespace}.sessionComplete`, [
							createdIssuesWithIssueData,
							triggerPointKey,
						]);
					} else {
						Events?.trigger?.(`${namespace}.sessionComplete`, [createdIssuesWithIssueData]);
					}
				});
				if (!fg('stop_polli_fails_due_to_load_bridge')) onClose?.(createdIssuesWithIssueData);
			}
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'publishCreatedIssues',
					packageName: 'jiraIssueCreate',
					teamName: 'gryffindor',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
		if (isSessionComplete) {
			issueDataTillNow = {};
			activePromises = {};
		}

		/**
		 * On cleanup we need to re-register the legacy issue create event handlers which will override the
		 * allow non-migrated triggers to show flag
		 */
		if (oldFlagUnbindSuccess && isSessionComplete) {
			browserWindow?.require('jira/common/header').initialize();
		}
	} else if (isSessionComplete) {
		if (fetchAllFields) executeEcosystemAPICallback(ecosystemCallback, []);
		onClose && onClose([]);
	}
};

export const getTabIndexWithError = (
	fieldTabs: FieldTabs,
	fieldValues: FormFieldValues | null,
	requiredFieldsData: FieldConfig,
): number => {
	if (!fieldValues) {
		return -1;
	}

	const emptyRequiredField: string | undefined = Object.keys(fieldValues)?.find(
		(id: string) =>
			requiredFieldsData[id] &&
			Boolean(requiredFieldsData[id].isRequired) &&
			fieldValues &&
			isEmpty(fieldValues[id]),
	);

	const tabIndex = fieldTabs?.findIndex((tab) =>
		tab.items.find((item) => item === emptyRequiredField),
	);
	return tabIndex > -1 ? tabIndex : -1;
};

export const getWatchersFlagConfigFromResponse = (
	response: IssueCreateResponse['watchers'],
	issueKey: string,
	issueType?: string,
): FlagConfiguration | undefined => {
	if (response?.errorCollection && response.errorCollection.errorMessages[0]) {
		return {
			type: 'error',
			title: watchersErrorFlagTitle,
			description: response.errorCollection.errorMessages[0],
			// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
			messageId: 'issue-create.ui.modal.flag.error.error.watchers-error',
			messageType: 'transactional',
		};
	}

	const getTitle = () => {
		if (issueType && expVal('jira-itr-work-type-exp-1', 'isEnabled', false)) {
			return watchersWarningFlagTitleIssueTerminologyRefreshM4;
		}
		if (expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)) {
			return watchersWarningFlagTitleIssueTermRefresh;
		}
		return watchersWarningFlagTitle;
	};

	if (response?.warningCollection && response?.warningCollection.warnings[0]) {
		return {
			type: 'warning',
			title: [getTitle(), { issueKey, issueType }],
			description: response.warningCollection.warnings[0],
			// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
			messageId: 'issue-create.ui.modal.flag.warning.warning.watchers-warning',
			messageType: 'transactional',
		};
	}

	return undefined;
};
